import React from 'react';
import { Search, Filter, Calendar, X } from 'lucide-react';
import { Button } from '../ui/Button/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { TRIP_STATUS, TRIP_TYPE } from '../../store/tripSlice';

/**
 * TripFilters Component
 * 
 * A comprehensive filtering component for trip data with search,
 * status, type, and date range filters.
 */
export function TripFilters({
  searchTerm = '',
  statusFilter = 'all',
  typeFilter = 'all',
  dateRange = 'all',
  onSearchChange,
  onStatusChange,
  onTypeChange,
  onDateRangeChange,
  onClearFilters,
  totalTrips = 0,
  filteredTrips = 0,
  className = ''
}) {
  const hasActiveFilters = searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || dateRange !== 'all';

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: TRIP_STATUS.COMPLETED, label: 'Completed' },
    { value: TRIP_STATUS.ONGOING, label: 'In Progress' },
    { value: TRIP_STATUS.ASSIGNED, label: 'Assigned' },
    { value: TRIP_STATUS.REQUESTED, label: 'Requested' },
    { value: TRIP_STATUS.CANCELLED, label: 'Cancelled' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: TRIP_TYPE.STANDARD, label: 'Standard' },
    { value: TRIP_TYPE.PREMIUM, label: 'Premium' },
    { value: TRIP_TYPE.SHARED, label: 'Shared' },
    { value: TRIP_TYPE.SCHEDULED, label: 'Scheduled' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'quarter', label: 'Last 3 Months' }
  ];

  return (
    <Card className={`p-6 ${className}`}>
      {/* Main Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {/* Search Input */}
        <div className="lg:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search trips, passengers, drivers..."
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => onSearchChange?.('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div>
          <select
            value={typeFilter}
            onChange={(e) => onTypeChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          >
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Filter */}
        <div>
          <select
            value={dateRange}
            onChange={(e) => onDateRangeChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          >
            {dateRangeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Summary and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{filteredTrips.toLocaleString()}</span> of{' '}
            <span className="font-medium">{totalTrips.toLocaleString()}</span> trips
          </div>

          {/* Active Filters Badges */}
          {hasActiveFilters && (
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Filters:</span>
              {searchTerm && (
                <Badge variant="info" size="sm">
                  Search: "{searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm}"
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="info" size="sm">
                  Status: {statusOptions.find(opt => opt.value === statusFilter)?.label}
                </Badge>
              )}
              {typeFilter !== 'all' && (
                <Badge variant="info" size="sm">
                  Type: {typeOptions.find(opt => opt.value === typeFilter)?.label}
                </Badge>
              )}
              {dateRange !== 'all' && (
                <Badge variant="info" size="sm">
                  Date: {dateRangeOptions.find(opt => opt.value === dateRange)?.label}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="flex items-center space-x-1"
          >
            <X className="w-4 h-4" />
            <span>Clear Filters</span>
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * Compact version of TripFilters for smaller spaces
 */
export function TripFiltersCompact({
  searchTerm = '',
  statusFilter = 'all',
  onSearchChange,
  onStatusChange,
  filteredTrips = 0,
  className = ''
}) {
  return (
    <div className={`flex items-center space-x-4 ${className}`}>
      {/* Search */}
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search trips..."
          value={searchTerm}
          onChange={(e) => onSearchChange?.(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Status Filter */}
      <div className="w-40">
        <select
          value={statusFilter}
          onChange={(e) => onStatusChange?.(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value={TRIP_STATUS.COMPLETED}>Completed</option>
          <option value={TRIP_STATUS.ONGOING}>In Progress</option>
          <option value={TRIP_STATUS.ASSIGNED}>Assigned</option>
          <option value={TRIP_STATUS.REQUESTED}>Requested</option>
          <option value={TRIP_STATUS.CANCELLED}>Cancelled</option>
        </select>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600 whitespace-nowrap">
        {filteredTrips} trips
      </div>
    </div>
  );
}

/**
 * Quick Filter Buttons for common filters
 */
export function QuickFilters({ 
  activeFilter, 
  onFilterChange, 
  tripCounts = {},
  className = '' 
}) {
  const quickFilters = [
    { 
      key: 'all', 
      label: 'All Trips', 
      count: tripCounts.total || 0 
    },
    { 
      key: TRIP_STATUS.ONGOING, 
      label: 'Active', 
      count: tripCounts.ongoing || 0,
      color: 'bg-blue-100 text-blue-800 hover:bg-blue-200'
    },
    { 
      key: TRIP_STATUS.COMPLETED, 
      label: 'Completed', 
      count: tripCounts.completed || 0,
      color: 'bg-green-100 text-green-800 hover:bg-green-200'
    },
    { 
      key: TRIP_STATUS.CANCELLED, 
      label: 'Cancelled', 
      count: tripCounts.cancelled || 0,
      color: 'bg-red-100 text-red-800 hover:bg-red-200'
    }
  ];

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {quickFilters.map((filter) => (
        <button
          key={filter.key}
          onClick={() => onFilterChange?.(filter.key)}
          className={`
            px-3 py-2 rounded-lg text-sm font-medium transition-colors
            ${activeFilter === filter.key 
              ? filter.color || 'bg-blue-100 text-blue-800' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          {filter.label}
          {filter.count > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-50 rounded-full text-xs">
              {filter.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
